import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

/**
 * Monthly batch status badge.
 *
 * Status values mirror the `monthly_batch_status` enum from
 * docs/specs/03-database-spec.md §3. The label is i18n-driven so that the
 * same component renders correctly across ja/zh-TW/en (08-pa-jp §9).
 */

export type BatchStatus =
  | 'draft'
  | 'open'
  | 'pending_approval'
  | 'closed'
  | 'reopened'
  | 'archived';

interface BatchStatusBadgeProps {
  status: BatchStatus;
  className?: string;
}

const toneClass: Record<BatchStatus, string> = {
  draft: 'efs-bg-info',
  open: 'efs-bg-success',
  pending_approval: 'efs-bg-warning',
  closed: 'bg-efs-bg-subtle text-efs-text-muted',
  reopened: 'efs-bg-warning',
  archived: 'bg-efs-bg-subtle text-efs-text-muted',
};

export const BatchStatusBadge = ({ status, className }: BatchStatusBadgeProps) => {
  const { t } = useTranslation();
  const label = t(`efs.common.status.${status}`, { defaultValue: status });

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium uppercase tracking-wide',
        toneClass[status],
        className,
      )}
    >
      {label}
    </span>
  );
};
